import { Component } from "react";
import PropTypes from "prop-types";
import { logger, buildErrorContext } from "../../config/telemetry";
import "./ErrorBoundary.css";

/**
 * ErrorBoundary - catches uncaught errors thrown during rendering,
 * in lifecycle methods, and in constructors of the whole child tree.
 *
 * Every caught error is logged to Rapid7 and a graceful fallback UI is shown
 * instead of a blank white screen.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Render the fallback UI on the next render.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the uncaught render error to Rapid7.
    logger.error("react.error_boundary", {
      ...buildErrorContext(),
      type: "UncaughtError",
      source: "ErrorBoundary",
      error_desc: error?.message,
      error_stack: error?.stack,
      component_stack: errorInfo?.componentStack,
    });
    // Keep it visible in the console for local debugging too.
    // eslint-disable-next-line no-console
    console.error("Uncaught error caught by ErrorBoundary:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary-content">
            <h1 className="error-boundary-title">Something went wrong</h1>
            <p className="error-boundary-message">
              An unexpected error occurred. Our team has been notified. Please
              try again.
            </p>
            <button
              type="button"
              className="error-boundary-button"
              onClick={this.handleReload}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
};

export default ErrorBoundary;
